import React, { useCallback, useEffect, useState } from "react";
import {
  Container, Droplets, Gauge, ShieldAlert, TrendingDown, TrendingUp,
} from "lucide-react";
import api from "../../lib/api";
import { fmt, fmtInt, shortDate } from "../../lib/format";
import { ConfidencePill } from "../Pills";

const RISK_STYLES = {
  low: { bg: "#10B98115", border: "#10B98140", text: "#10B981", label: "Low" },
  medium: { bg: "#F59E0B15", border: "#F59E0B40", text: "#F59E0B", label: "Medium" },
  high: { bg: "#F9731615", border: "#F9731640", text: "#F97316", label: "High" },
  critical: { bg: "#EF444415", border: "#EF444440", text: "#EF4444", label: "Critical" },
};

function Stat({ Icon, label, value, unit, accent, sub, testid }) {
  return (
    <div className="flex flex-col gap-1" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-muted-foreground">
        <Icon className="w-3 h-3" style={{ color: accent }} />
        <span className="label-tiny">{label}</span>
      </div>
      <div className="font-heading text-2xl font-semibold tabular-nums" style={{ color: accent }}>
        {value}
        {unit && <span className="text-xs text-muted-foreground ml-1 font-normal">{unit}</span>}
      </div>
      {sub && <div className="text-[11px] text-muted-foreground">{sub}</div>}
    </div>
  );
}

/**
 * Water Snapshot card for the Dashboard — pulls /water/snapshot once and
 * surfaces tank %, autonomy days, drought risk, and consumption trend vs
 * last month. Designed to live above the existing WaterSection grid.
 */
export default function WaterSnapshotCard({ siteId }) {
  const [snap, setSnap] = useState(null);
  const [err, setErr] = useState(null);

  const reload = useCallback(async () => {
    if (!siteId) return;
    try {
      const { data } = await api.get(`/sites/${siteId}/water/snapshot`);
      setSnap(data);
      setErr(null);
    } catch (e) {
      setErr(e.userMessage || "Could not load water snapshot");
    }
  }, [siteId]);

  useEffect(() => { reload(); }, [reload]);

  if (err) {
    return (
      <section className="card-base p-5" data-testid="water-snapshot-card">
        <div className="text-xs text-[#EF4444]" data-testid="water-snapshot-error">{err}</div>
      </section>
    );
  }

  const risk = snap?.drought_risk;
  const riskStyle = RISK_STYLES[risk?.level || "low"];
  const trend = snap?.trend_vs_last_month_pct;
  const trendColor = trend == null ? "#71717A" : trend > 0 ? "#F97316" : "#10B981";
  const TrendIcon = trend == null ? Droplets : trend > 0 ? TrendingUp : TrendingDown;
  const pctSub =
    snap?.tank_pct != null && snap?.tank_capacity_liters
      ? `${snap.tank_pct.toFixed(0)}% of ${fmtInt(snap.tank_capacity_liters)} L`
      : "Add a tank anchor on the Water page";

  return (
    <section className="card-base p-5" data-testid="water-snapshot-card">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Droplets className="w-3.5 h-3.5 text-[#3B82F6]" />
          <h2 className="label-tiny">Water snapshot</h2>
          {snap?.tank_source && (
            <ConfidencePill level={snap.tank_source === "simulated" ? "simulated" : "manual"} />
          )}
        </div>
        {risk && (
          <div
            className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md border text-[11px] font-medium"
            style={{
              backgroundColor: riskStyle.bg,
              borderColor: riskStyle.border,
              color: riskStyle.text,
            }}
            data-testid="water-drought-risk-pill"
          >
            <ShieldAlert className="w-3 h-3" />
            Drought risk · {riskStyle.label}
            <span className="opacity-70">({risk.score})</span>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
        <Stat
          Icon={Container}
          label="Current volume"
          value={snap?.tank_level_liters == null ? "—" : fmtInt(snap.tank_level_liters)}
          unit={snap?.tank_level_liters == null ? null : "L"}
          accent="#3B82F6"
          sub={pctSub}
          testid="water-snap-volume"
        />
        <Stat
          Icon={Gauge}
          label="Autonomy"
          value={snap?.autonomy_days == null ? "—" : fmt(snap.autonomy_days, 1)}
          unit={snap?.autonomy_days == null ? null : "days"}
          accent="#10B981"
          sub={
            snap?.projected_depletion_date
              ? `Empty by ${shortDate(snap.projected_depletion_date)}`
              : snap?.avg_daily_inflow_l > 0
                ? "Inflow keeps up with use"
                : "Add daily-use estimate"
          }
          testid="water-snap-autonomy"
        />
        <Stat
          Icon={ShieldAlert}
          label="Drought risk"
          value={risk?.score == null ? "—" : risk.score}
          unit={risk?.level ? `/ 100` : null}
          accent={riskStyle.text}
          sub={risk?.reasons?.[0] || (risk?.level === "low" ? "No drought signals" : null)}
          testid="water-snap-risk"
        />
        <Stat
          Icon={TrendIcon}
          label="Trend vs last month"
          value={
            trend == null ? "—" : `${trend > 0 ? "+" : ""}${trend.toFixed(0)}`
          }
          unit={trend == null ? null : "%"}
          accent={trendColor}
          sub={
            snap?.monthly_consumption_liters
              ? `${fmtInt(snap.monthly_consumption_liters)} L this month`
              : "Not enough history yet"
          }
          testid="water-snap-trend"
        />
      </div>

      {(snap?.warnings || []).length > 0 && (
        <ul className="mt-4 space-y-1.5" data-testid="water-snap-warnings">
          {snap.warnings.map((w, i) => (
            <li
              key={i}
              className="text-[11px] px-2.5 py-1.5 rounded-md border"
              style={{
                color: w.severity === "critical" ? "#EF4444" : "#F59E0B",
                borderColor: w.severity === "critical" ? "#EF444440" : "#F59E0B40",
                backgroundColor: w.severity === "critical" ? "#EF444410" : "#F59E0B10",
              }}
            >
              {w.message}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
